import React, { memo, useEffect, useState } from "react";

import { ActionsMenu, MenuItem, ActionMenuDivider } from "ui/actions-menu";
import CloneProgramTemplatesModal from "components/modals/clone-program-templates-modal/clone-program-templates-modal";
import CreateTouchpointFromProgramTemplateModal from "components/modals/create-touchpoint-from-program-template-modal/create-touchpoint-from-program-template-modal";
import UpdateClientThemeModal from "components/modals/update-client-theme-modal/update-client-theme-modal";
import { useArchiveTouchpointModal } from "containers/admin/clients/campaign/campaign-creative-page/hooks/modals/use-archive-touchpoint-modal";

import type { CampaignTouchpointSummaryDTO, CampaignTouchpointSummaryDTOType } from "generated/schemas";
import type { DisplayedProgramTemplate } from "containers/admin/clients/client/program-templates-page/components/program-templates-table/program-templates-table.helper";
import { TouchpointAttributes } from "hooks/api/use-touchpoints";

import { useCurrentClient } from "hooks/api/use-clients";
import { canCreateTouchpointFromTemplate } from "utilities/touchpoint";

import Icons from "assets";
import {
  type TouchpointActionItem,
  type TouchpointMenuItem,
} from "containers/admin/clients/campaign/campaign-creative-page/hooks/use-touchpoint-actions";

interface ProgramTemplatesActionsMenuProps {
  template: CampaignTouchpointSummaryDTO;
  programTemplatesCampaignId: string;
  onCloneSuccess?: () => void;
}

const getCreateFromTemplateConfig = (
  type: CampaignTouchpointSummaryDTOType,
  iconClass: string,
) => {
  switch (type) {
    case "EMAIL":
      return { icon: <Icons.Email className={iconClass} />, label: "Create email from template" };
    case "LANDING_PAGE":
      return {
        icon: <Icons.LandingPage className={iconClass} />,
        label: "Create web page from template",
      };
    default:
      return {
        icon: <Icons.Other className={iconClass} />,
        label: "Create touchpoint from template",
      };
  }
};

const ProgramTemplatesActionsMenu: React.FC<ProgramTemplatesActionsMenuProps> = ({
  template,
  programTemplatesCampaignId,
  onCloneSuccess,
}) => {
  const currentClient = useCurrentClient();
  const [isOpen, setIsOpen] = useState(false);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [isCreateFromTemplateModalOpen, setIsCreateFromTemplateModalOpen] = useState(false);
  const [isUpdateClientThemeModalOpen, setIsUpdateClientThemeModalOpen] = useState(false);

  const handleMenuOpen = () => setIsOpen(true);
  const handleMenuClose = () => setIsOpen(false);

  const { openArchiveModal, archiveModalMarkup } = useArchiveTouchpointModal({
    onSuccess: onCloneSuccess,
    apiContext: { campaignId: programTemplatesCampaignId },
    isTemplate: true,
  });

  const iconClass = "size-5 text-gray-600";
  const isEmail = template.type === "EMAIL";
  const canCreateTouchpoint = canCreateTouchpointFromTemplate(template.latestVersion?.status);
  const emptyContent = template.latestVersion?.emptyContent;
  const canApplyTheme =
    isEmail && !emptyContent && !!template.latestVersion?.applyClientTheme;

  // Handle closing menu on scroll
  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = () => {
      setIsOpen(false);
    };

    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [isOpen]);

  const templateAsDisplayed: DisplayedProgramTemplate = {
    id: template.id,
    name: template.name,
    type: template.type,
    latestVersion: template.latestVersion
      ? {
          version: template.latestVersion.version ?? 0,
          status: template.latestVersion.status,
          id: template.latestVersion.id,
          applyClientTheme: template.latestVersion.applyClientTheme,
          emptyContent,
        }
      : null,
  };

  const actions: TouchpointMenuItem[] = [];

  if (canApplyTheme) {
    actions.push({
      icon: <Icons.Theme className={iconClass} />,
      label: "Apply theme",
      onClick: () => setIsUpdateClientThemeModalOpen(true),
    });
  }

  actions.push(
    {
      icon: <Icons.Clone className={iconClass} />,
      label: "Clone template",
      onClick: () => setIsCloneModalOpen(true),
    },
    ...(canCreateTouchpoint
      ? [
          {
            ...getCreateFromTemplateConfig(template.type, iconClass),
            onClick: () => setIsCreateFromTemplateModalOpen(true),
          },
        ]
      : []),
    { type: "divider" },
    {
      icon: <Icons.Archive className={iconClass} />,
      label: "Archive template",
      onClick: () =>
        openArchiveModal({
          id: template.id,
          type: template.type,
        } as TouchpointAttributes),
    },
  );

  return (
    <>
      <ActionsMenu
        itemName={template.name || "template"}
        open={isOpen}
        onOpen={handleMenuOpen}
        onClose={handleMenuClose}
        sx={{ justifyContent: "flex-end", pr: 0.75 }}>
        {actions.map((item, index) => {
          if ("type" in item && item.type === "divider") {
            return <ActionMenuDivider key={`divider-${index}`} />;
          }

          const action = item as TouchpointActionItem;
          return (
            <MenuItem
              key={action.label}
              icon={action.icon}
              label={action.label}
              onClick={() => {
                action.onClick();
                setIsOpen(false);
              }}
            />
          );
        })}
      </ActionsMenu>

      <CloneProgramTemplatesModal
        isOpen={isCloneModalOpen}
        onClose={() => setIsCloneModalOpen(false)}
        selectedTemplates={[templateAsDisplayed]}
        onSuccess={onCloneSuccess}
      />

      <CreateTouchpointFromProgramTemplateModal
        isOpen={isCreateFromTemplateModalOpen}
        onClose={() => setIsCreateFromTemplateModalOpen(false)}
        selectedTemplates={[templateAsDisplayed]}
      />

      {canApplyTheme && template.latestVersion?.id && (
        <UpdateClientThemeModal
          isOpen={isUpdateClientThemeModalOpen}
          setIsOpen={setIsUpdateClientThemeModalOpen}
          touchpointVersionId={template.latestVersion.id}
          clientId={currentClient.id}
          campaignId={programTemplatesCampaignId}
          touchpointId={template.id}
        />
      )}

      {archiveModalMarkup}
    </>
  );
};

export default memo(ProgramTemplatesActionsMenu);
